/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { datadogRum } from "@datadog/browser-rum"
import { Middleware, UnknownAction } from "@reduxjs/toolkit"
import { isFetchLifecycleRumEnabled } from "constants/datadog"
import { AppState } from "store/store.type"

export const FETCH_ATTEMPT_ACTION = "fetch_attempt"
export const FETCH_STATE_UPDATED_ACTION = "fetch_state_updated"

interface RequestPayload {
  request?: {
    url?: string
  }
}

interface ActionMeta {
  previousAction?: {
    type?: string
    payload?: RequestPayload
  }
  arg?: {
    endpointName?: string
  }
}

type LifecycleAction = UnknownAction & {
  payload?: RequestPayload
  meta?: ActionMeta
}

const normalizeUrl = (url?: string): string => url?.split(/[?#]/)[0] ?? ""

const linkPage = (): string =>
  window?.location?.pathname?.split(/[?#]/)[0] ?? ""

const reportRumAction = (
  name: string,
  context: Record<string, string | undefined>,
): void => {
  if (!isFetchLifecycleRumEnabled()) {
    return
  }

  try {
    datadogRum.addAction(name, { ...context, link_page: linkPage() })
  } catch {
    // Datadog may be unavailable outside instrumented sessions.
  }
}

const isRtkPending = (type: string): boolean =>
  type.endsWith("/executeQuery/pending") ||
  type.endsWith("/executeMutation/pending") ||
  type.endsWith("/executeInfiniteQuery/pending")

const isRtkTerminal = (type: string): boolean =>
  type.endsWith("/executeQuery/fulfilled") ||
  type.endsWith("/executeQuery/rejected") ||
  type.endsWith("/executeMutation/fulfilled") ||
  type.endsWith("/executeMutation/rejected") ||
  type.endsWith("/executeInfiniteQuery/fulfilled") ||
  type.endsWith("/executeInfiniteQuery/rejected")

const reportLegacyStateUpdated = (action: LifecycleAction): void => {
  const actionType = action.type as string
  const previousAction = action.meta?.previousAction
  if (!previousAction?.type) {
    return
  }

  const previousType = previousAction.type as string
  const isSuccess = actionType === `${previousType}_SUCCESS`
  const isError =
    actionType === `${previousType}_FAIL` ||
    actionType === `${previousType}_FAILED`

  if (!isSuccess && !isError) {
    return
  }

  reportRumAction(FETCH_STATE_UPDATED_ACTION, {
    architecture_type: "old_redux",
    action_type: previousType,
    endpoint_url: normalizeUrl(previousAction.payload?.request?.url),
    status: isSuccess ? "success" : "error",
  })
}

const reportRtkStateUpdated = (action: LifecycleAction): void => {
  const actionType = action.type as string
  if (!isRtkTerminal(actionType)) {
    return
  }

  reportRumAction(FETCH_STATE_UPDATED_ACTION, {
    architecture_type: "rtk_query",
    endpoint_name: action.meta?.arg?.endpointName,
    status: actionType.endsWith("/fulfilled") ? "success" : "error",
  })
}

/** Before axios — catches outgoing legacy requests and RTK pending actions. */
export const fetchLifecycleRumAttemptMiddleware: Middleware<
  {},
  AppState
> = () => next => (action: UnknownAction) => {
  const lifecycleAction = action as LifecycleAction
  const actionType = lifecycleAction.type as string
  const requestUrl = lifecycleAction.payload?.request?.url

  if (requestUrl) {
    reportRumAction(FETCH_ATTEMPT_ACTION, {
      architecture_type: "old_redux",
      action_type: actionType,
      endpoint_url: normalizeUrl(requestUrl),
    })
  }

  if (isRtkPending(actionType)) {
    reportRumAction(FETCH_ATTEMPT_ACTION, {
      architecture_type: "rtk_query",
      endpoint_name: lifecycleAction.meta?.arg?.endpointName,
    })
  }

  return next(action)
}

/**
 * After axios — legacy SUCCESS/FAIL are dispatched via axios middleware's
 * `next()`, so they never re-enter middleware placed before axios.
 */
export const fetchLifecycleRumCompleteMiddleware: Middleware<
  {},
  AppState
> = () => next => (action: UnknownAction) => {
  const lifecycleAction = action as LifecycleAction
  const result = next(action)

  reportLegacyStateUpdated(lifecycleAction)
  reportRtkStateUpdated(lifecycleAction)

  return result
}
